import { useRef, useCallback, useState, useEffect } from "react";
import { toast } from "sonner";
import { Upload, Plus, Trash2, Info, UserPlus, Undo2, Redo2, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "./ui/button.js";
import { Badge } from "./ui/badge.js";
import { todayChile } from "../../lib/chileTime.js";
import { validarRut, formatRut } from "../../lib/rut.ts";
import { sanitizeBheText } from "../../lib/siiSanitize.js";
import { api } from "../api.js";
import type { ClienteFrecuenteItem } from "./ClienteSelectorModal.js";

export interface Column<T> {
  key: keyof T;
  label: string;
  type?: "text" | "number" | "date" | "select";
  placeholder?: string;
  required?: boolean;
  width?: string;
  validate?: "rut" | "comuna";
  options?: { value: string; label: string }[];
  dynamicOptions?: "region" | "comuna";
  highlight?: boolean;
  help?: string;
}

export type RowStatus = "idle" | "pending" | "emitting" | "success" | "error";

export interface RowState {
  status: RowStatus;
  folio?: number;
  error?: string;
}

interface EditableTableProps<T extends Record<string, unknown>> {
  columns: Column<T>[];
  rows: T[];
  onChange: (rows: T[]) => void;
  rowStates: RowState[];
  onRowStatesChange: (states: RowState[]) => void;
  emptyRow: () => T;
  maxRows?: number;
  validateRow?: (row: T) => string | null;
  onOpenClienteSelector?: () => void;
  clientes?: ClienteFrecuenteItem[];
  onSelectClienteInRow?: (rowIdx: number, cliente: ClienteFrecuenteItem) => void;
}

function statusBadge(status: RowStatus, folio?: number, error?: string) {
  switch (status) {
    case "pending": return <Badge variant="secondary" className="gap-1">⏳</Badge>;
    case "emitting": return <Badge variant="warning" className="gap-1 animate-pulse">🔄 Emitiendo</Badge>;
    case "success": return <Badge variant="success" className="gap-1">✓ {folio}</Badge>;
    case "error": return <Badge variant="destructive" className="gap-1 max-w-[220px] truncate text-xs cursor-help" title={error}>✗ {error}</Badge>;
    default: return null;
  }
}

function parseClipboard(text: string): string[][] {
  const lines = text.replace(/\r\n/g, "\n").trim().split("\n");
  return lines.map((line) => {
    if (line.includes("\t")) return line.split("\t");
    if (line.includes(";")) return line.split(";").map((s) => s.trim().replace(/^"|"$/g, ""));
    if (line.includes(",")) return line.split(",").map((s) => s.trim().replace(/^"|"$/g, ""));
    return [line.trim()];
  });
}

function normalizeDate(value: string): string {
  const v = value.trim();
  if (!v) return v;

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;

  // DD/MM/YYYY or DD-MM-YYYY
  let m = v.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) {
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }

  // DD/MM/YY or DD-MM-YY
  m = v.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2})$/);
  if (m) {
    const [, dd, mm, yy] = m;
    return `20${yy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }

  // DD/MM/YYYY HH:mm (Excel with time)
  m = v.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})\s+\d{1,2}:\d{2}/);
  if (m) {
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }

  return v;
}

function isMultiCellData(text: string): boolean {
  return text.includes("\n") || text.includes("\t") || text.includes(",") || text.includes(";");
}

function normalizeText(str: string): string {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/^region\s+/, "")
    .replace(/\s+/g, " ")
    .trim();
}

const REGION_ALIASES: Record<string, string> = {
  "rm": "Metropolitana",
  "r.m.": "Metropolitana",
  "xiii": "Metropolitana",
  "xv": "Arica y Parinacota",
  "xiv": "Los Rios",
  "ix": "Araucania",
  "vi": "Ohiggins",
  "vii": "Maule",
  "viii": "Biobio",
  "xi": "Aysen",
  "xii": "Magallanes",
  "i": "Tarapaca",
  "ii": "Antofagasta",
  "iii": "Atacama",
  "iv": "Coquimbo",
  "v": "Valparaiso",
  "x": "Los Lagos",
};

function matchRegion(input: string, regiones: string[]): string | null {
  const norm = normalizeText(input);
  if (!norm) return null;

  // 1. Exact match (normalized)
  for (const r of regiones) {
    if (normalizeText(r) === norm) return r;
  }

  // 2. Alias lookup
  const aliasVal = REGION_ALIASES[norm];
  if (aliasVal) {
    for (const r of regiones) {
      if (normalizeText(r) === normalizeText(aliasVal)) return r;
    }
  }

  // 3. Partial: starts with
  for (const r of regiones) {
    if (normalizeText(r).startsWith(norm) || norm.startsWith(normalizeText(r))) return r;
  }

  // 4. Partial: contains
  for (const r of regiones) {
    if (normalizeText(r).includes(norm) || norm.includes(normalizeText(r))) return r;
  }

  return null;
}

function matchComuna(
  input: string,
  comunas: { region: string; regionNombre: string; nombre: string }[],
  regionNombre?: string,
): string | null {
  const norm = normalizeText(input);
  if (!norm) return null;

  const pool = regionNombre
    ? comunas.filter((c) => c.regionNombre === regionNombre)
    : comunas;

  // 1. Exact match (normalized)
  for (const c of pool) {
    if (normalizeText(c.nombre) === norm) return c.nombre;
  }

  // 2. Starts with
  for (const c of pool) {
    if (normalizeText(c.nombre).startsWith(norm)) return c.nombre;
  }

  // 3. Contains
  for (const c of pool) {
    if (normalizeText(c.nombre).includes(norm)) return c.nombre;
  }

  // 4. Fallback: search in all comunas (not just region-filtered)
  if (regionNombre) {
    for (const c of comunas) {
      if (normalizeText(c.nombre) === norm) return c.nombre;
    }
    for (const c of comunas) {
      if (normalizeText(c.nombre).startsWith(norm)) return c.nombre;
    }
  }

  return null;
}

function sortByHighlightCol<T extends Record<string, unknown>>(
  rows: T[],
  states: RowState[],
  highlightKey: keyof T,
): { rows: T[]; states: RowState[]; distinctCount: number } {
  const distinctValues = new Set(rows.map((r) => String(r[highlightKey] ?? "").trim()).filter(Boolean));
  if (distinctValues.size <= 1) return { rows, states, distinctCount: distinctValues.size };

  // Stable sort: preserve relative order within each group
  const indexed = rows.map((row, i) => ({ row, state: states[i] ?? { status: "idle" as const }, idx: i }));
  indexed.sort((a, b) => {
    const aVal = String(a.row[highlightKey] ?? "").trim();
    const bVal = String(b.row[highlightKey] ?? "").trim();
    if (aVal === bVal) return a.idx - b.idx;
    if (!aVal) return 1;
    if (!bVal) return -1;
    return aVal.localeCompare(bVal);
  });

  return {
    rows: indexed.map((x) => x.row),
    states: indexed.map((x) => x.state),
    distinctCount: distinctValues.size,
  };
}

interface Selection {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
}

function normalizeSelection(sel: Selection): { minRow: number; maxRow: number; minCol: number; maxCol: number } {
  return {
    minRow: Math.min(sel.startRow, sel.endRow),
    maxRow: Math.max(sel.startRow, sel.endRow),
    minCol: Math.min(sel.startCol, sel.endCol),
    maxCol: Math.max(sel.startCol, sel.endCol),
  };
}

function isCellInSelection(row: number, col: number, sel: Selection): boolean {
  const n = normalizeSelection(sel);
  return row >= n.minRow && row <= n.maxRow && col >= n.minCol && col <= n.maxCol;
}

export function EditableTable<T extends Record<string, unknown>>({
  columns,
  rows,
  onChange,
  rowStates,
  onRowStatesChange,
  emptyRow,
  maxRows = 500,
  validateRow,
  onOpenClienteSelector,
  clientes,
  onSelectClienteInRow,
}: EditableTableProps<T>) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const [selection, setSelection] = useState<Selection | null>(null);
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [comunas, setComunas] = useState<{ region: string; regionNombre: string; nombre: string }[]>([]);
  const regiones = [...new Set(comunas.map((c) => c.regionNombre))].sort((a, b) => a.localeCompare(b));

  // ── Detección de Desbordamiento Horizontal (Scroll Shadows & Affordance) ──
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollIndicators = useCallback(() => {
    const el = tableRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollIndicators();
    const el = tableRef.current;
    if (!el) return;
    const handleResize = () => updateScrollIndicators();
    window.addEventListener("resize", handleResize);
    const ro = new ResizeObserver(() => updateScrollIndicators());
    ro.observe(el);
    return () => {
      window.removeEventListener("resize", handleResize);
      ro.disconnect();
    };
  }, [rows.length, columns, updateScrollIndicators]);

  // ── Historial Undo / Redo en Memoria (Borradores) ──
  const historyRef = useRef<{ past: T[][]; future: T[][] }>({ past: [], future: [] });
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const pushHistory = useCallback((currentRows: T[]) => {
    if (currentRows.length === 0 && historyRef.current.past.length === 0) return;
    historyRef.current.past.push(JSON.parse(JSON.stringify(currentRows)));
    if (historyRef.current.past.length > 50) {
      historyRef.current.past.shift();
    }
    historyRef.current.future = []; // Nueva acción invalida el redo futuro
    setCanUndo(true);
    setCanRedo(false);
  }, []);

  const undo = useCallback(() => {
    if (historyRef.current.past.length === 0) return;
    const previous = historyRef.current.past.pop()!;
    historyRef.current.future.push(JSON.parse(JSON.stringify(rows)));
    setCanUndo(historyRef.current.past.length > 0);
    setCanRedo(true);

    // Blindaje de seguridad: filas emitidas o emitiéndose quedan congeladas
    const safeRows = previous.map((prevRow, idx) => {
      const st = rowStates[idx];
      if (st && (st.status === "success" || st.status === "emitting")) {
        return rows[idx] ?? prevRow;
      }
      return prevRow;
    });

    onChange(safeRows);
    toast.info("Deshecho (Ctrl+Z)", { duration: 1200 });
  }, [rows, rowStates, onChange]);

  const redo = useCallback(() => {
    if (historyRef.current.future.length === 0) return;
    const next = historyRef.current.future.pop()!;
    historyRef.current.past.push(JSON.parse(JSON.stringify(rows)));
    setCanUndo(true);
    setCanRedo(historyRef.current.future.length > 0);

    // Blindaje de seguridad: filas emitidas o emitiéndose quedan congeladas
    const safeRows = next.map((nextRow, idx) => {
      const st = rowStates[idx];
      if (st && (st.status === "success" || st.status === "emitting")) {
        return rows[idx] ?? nextRow;
      }
      return nextRow;
    });

    onChange(safeRows);
    toast.info("Rehecho (Ctrl+Y)", { duration: 1200 });
  }, [rows, rowStates, onChange]);

  useEffect(() => {
    if (!columns.some((c) => c.dynamicOptions === "region" || c.dynamicOptions === "comuna")) return;
    api<{ region: string; regionNombre: string; nombre: string }[]>("/comunas")
      .then((data) => {
        if (Array.isArray(data)) setComunas(data);
      })
      .catch(() => {});
  }, []);

  // Sync hidden input focus for keyboard capture
  useEffect(() => {
    if (selection && !editingCell) {
      hiddenInputRef.current?.focus();
    }
  }, [selection, editingCell]);

  // ── Auto-scroll al mover selección o cursor (Navegación fluida estilo Excel) ──
  useEffect(() => {
    if (!selection && !editingCell) return;
    const targetRow = editingCell ? editingCell.row : selection ? selection.endRow : null;
    const targetCol = editingCell ? editingCell.col : selection ? selection.endCol : null;
    if (targetRow === null || targetCol === null) return;

    const tableContainer = tableRef.current;
    if (!tableContainer) return;

    const cellEl = tableContainer.querySelector<HTMLElement>(`td[data-row="${targetRow}"][data-col="${targetCol}"]`);
    if (!cellEl) return;

    const containerRect = tableContainer.getBoundingClientRect();
    const cellRect = cellEl.getBoundingClientRect();

    // Margen para no quedar tapado bajo las flechas flotantes / sombras laterales
    const paddingLeft = 40;
    const paddingRight = 48;

    if (cellRect.left < containerRect.left + paddingLeft) {
      tableContainer.scrollLeft -= (containerRect.left + paddingLeft - cellRect.left);
    } else if (cellRect.right > containerRect.right - paddingRight) {
      tableContainer.scrollLeft += (cellRect.right - (containerRect.right - paddingRight));
    }
  }, [selection?.endRow, selection?.endCol, editingCell?.row, editingCell?.col]);

  const updateCells = (rowIdx: number, updates: Partial<Record<keyof T, string>>) => {
    const currentRow = rows[rowIdx];
    if (!currentRow) return;
    const hasChanges = Object.entries(updates).some(([k, v]) => String(currentRow[k as keyof T] ?? "") !== v);
    if (!hasChanges) return;

    pushHistory(rows);
    const newRows = [...rows];
    newRows[rowIdx] = { ...newRows[rowIdx], ...updates };
    onChange(newRows);
    if (rowStates[rowIdx]?.status === "error") {
      const newStates = [...rowStates];
      newStates[rowIdx] = { status: "idle" };
      onRowStatesChange(newStates);
    }
  };

  const updateCell = (rowIdx: number, key: keyof T, value: string) => {
    updateCells(rowIdx, { [key]: value } as unknown as Partial<Record<keyof T, string>>);
  };

  const addRow = () => {
    if (rows.length >= maxRows) return;
    pushHistory(rows);
    onChange([...rows, emptyRow()]);
    onRowStatesChange([...rowStates, { status: "idle" }]);
  };

  const removeRow = (idx: number) => {
    pushHistory(rows);
    onChange(rows.filter((_, i) => i !== idx));
    onRowStatesChange(rowStates.filter((_, i) => i !== idx));
    if (selection) {
      const n = normalizeSelection(selection);
      if (idx >= n.minRow && idx <= n.maxRow) setSelection(null);
      else if (idx < n.minRow) {
        setSelection({ ...selection, startRow: selection.startRow - 1, endRow: selection.endRow - 1 });
      }
    }
    if (editingCell?.row === idx) setEditingCell(null);
  };

  const clearAll = () => {
    pushHistory(rows);
    onChange([]);
    onRowStatesChange([]);
    setSelection(null);
    setEditingCell(null);
  };

  // ── Cell selection ──
  const handleCellMouseDown = (e: React.MouseEvent, rowIdx: number, colIdx: number) => {
    if (editingCell) return; // Don't interfere while editing
    e.preventDefault();
    hiddenInputRef.current?.focus();

    if (e.shiftKey && selection) {
      setSelection({ ...selection, endRow: rowIdx, endCol: colIdx });
    } else {
      setSelection({ startRow: rowIdx, startCol: colIdx, endRow: rowIdx, endCol: colIdx });
    }
    setIsDragging(true);
  };

  const handleCellMouseEnter = (rowIdx: number, colIdx: number) => {
    if (!isDragging || !selection) return;
    setSelection({ ...selection, endRow: rowIdx, endCol: colIdx });
  };

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, []);

  // ── Start editing ──
  const startEditing = (rowIdx: number, colIdx: number) => {
    const col = columns[colIdx];
    if (!col || col.type === "select") return;
    const currentVal = String(rows[rowIdx]?.[col.key] ?? "");
    setEditValue(currentVal);
    setEditingCell({ row: rowIdx, col: colIdx });
    setSelection({ startRow: rowIdx, startCol: colIdx, endRow: rowIdx, endCol: colIdx });
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const { row, col } = editingCell;
    const colDef = columns[col];
    if (!colDef) { setEditingCell(null); return; }
    const finalVal = colDef.type === "date" ? normalizeDate(editValue) : editValue;
    let saveVal = colDef.validate === "rut" && finalVal.trim() ? formatRut(finalVal.trim()) : finalVal;

    if (colDef.validate === "rut" && finalVal.trim() && !validarRut(finalVal.trim())) {
      toast.error(`RUT inválido en fila ${row + 1}: verifica el dígito verificador`, { duration: 4000 });
      updateCell(row, colDef.key, saveVal);
      const newStates = [...rowStates];
      newStates[row] = { status: "error", error: `RUT inválido en ${colDef.label}` };
      onRowStatesChange(newStates);
      setEditingCell(null);
      return;
    }

    if (colDef.validate === "rut" && finalVal.trim() && validarRut(finalVal.trim())) {
      if (rowStates[row]?.status === "error" && rowStates[row]?.error?.includes("RUT")) {
        const newStates = [...rowStates];
        newStates[row] = { status: "idle" };
        onRowStatesChange(newStates);
      }
    }

    if (colDef.type !== "number" && colDef.type !== "date" && colDef.type !== "select" && colDef.validate !== "rut") {
      const { cleaned, hadInvalid } = sanitizeBheText(saveVal);
      if (hadInvalid) {
        toast.info("Ajustamos caracteres para compatibilidad con el SII (# → N°, & → y, tildes)", { duration: 3000 });
        saveVal = cleaned;
      }
    }
    updateCell(row, colDef.key, saveVal);
    setEditingCell(null);

    // Auto-rellenar campos vinculados si es receptorRut y existe cliente frecuente
    if (colDef.key === "receptorRut" && saveVal.trim() && clientes && onSelectClienteInRow) {
      const cleanRut = saveVal.replace(/[.\s-]/g, "").toUpperCase();
      const currentEmisor = String(rows[row]?.["rutEmisor"] ?? "").replace(/[.\s-]/g, "").toUpperCase();
      const matched = clientes.find((c) => {
        const cRut = c.rut.replace(/[.\s-]/g, "").toUpperCase();
        if (cRut !== cleanRut) return false;
        if (currentEmisor) {
          const cEmisor = c.rutEmisor.replace(/[.\s-]/g, "").toUpperCase();
          return cEmisor === currentEmisor;
        }
        return true;
      });
      if (matched) {
        onSelectClienteInRow(row, matched);
      }
    }
  };

  const cancelEdit = () => {
    setEditingCell(null);
  };

  // ── Keyboard navigation ──
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (editingCell) {
      if (e.key === "Enter") {
        e.preventDefault();
        commitEdit();
        // Move to next row
        const { row, col } = editingCell;
        if (row < rows.length - 1) {
          setSelection({ startRow: row + 1, startCol: col, endRow: row + 1, endCol: col });
        } else if (rows.length < maxRows) {
          addRow();
          setTimeout(() => {
            setSelection({ startRow: row + 1, startCol: col, endRow: row + 1, endCol: col });
          }, 0);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelEdit();
      } else if (e.key === "Tab") {
        e.preventDefault();
        commitEdit();
        const { row, col } = editingCell;
        if (e.shiftKey) {
          if (col > 0) setSelection({ startRow: row, startCol: col - 1, endRow: row, endCol: col - 1 });
          else if (row > 0) setSelection({ startRow: row - 1, startCol: columns.length - 1, endRow: row - 1, endCol: columns.length - 1 });
        } else {
          if (col < columns.length - 1) setSelection({ startRow: row, startCol: col + 1, endRow: row, endCol: col + 1 });
          else if (row < rows.length - 1) setSelection({ startRow: row + 1, startCol: 0, endRow: row + 1, endCol: 0 });
        }
      }
      return;
    }

    if (!selection) return;
    const n = normalizeSelection(selection);
    const curRow = n.minRow;
    const curCol = n.minCol;

    // ── Atajos de Edición y Portapapeles (Ctrl / Cmd) ──
    if (e.ctrlKey || e.metaKey) {
      // Ctrl + Z: Deshacer / Ctrl + Shift + Z: Rehacer
      if (e.key === "z" || e.key === "Z") {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        return;
      }

      // Ctrl + Y: Rehacer
      if (e.key === "y" || e.key === "Y") {
        e.preventDefault();
        redo();
        return;
      }

      // Ctrl + A: Seleccionar toda la tabla
      if (e.key === "a" || e.key === "A") {
        e.preventDefault();
        if (rows.length > 0 && columns.length > 0) {
          setSelection({
            startRow: 0,
            startCol: 0,
            endRow: rows.length - 1,
            endCol: columns.length - 1,
          });
        }
        return;
      }

      // Ctrl + D: Rellenar hacia abajo (Fill Down desde la primera fila seleccionada)
      if (e.key === "d" || e.key === "D") {
        e.preventDefault();
        if (n.minRow < n.maxRow) {
          pushHistory(rows);
          const newRows = [...rows];
          const topRow = newRows[n.minRow];
          if (topRow) {
            for (let r = n.minRow + 1; r <= n.maxRow; r++) {
              if (rowStates[r]?.status === "success" || rowStates[r]?.status === "emitting") continue;
              newRows[r] = { ...newRows[r] };
              for (let c = n.minCol; c <= n.maxCol; c++) {
                const col = columns[c];
                if (col) {
                  newRows[r][col.key] = topRow[col.key];
                }
              }
            }
            onChange(newRows);
            toast.success("Rellenado hacia abajo (Ctrl+D)", { duration: 1500 });
          }
        }
        return;
      }

      // Ctrl + X: Cortar rango seleccionado
      if (e.key === "x" || e.key === "X") {
        e.preventDefault();
        pushHistory(rows);
        const lines: string[] = [];
        for (let r = n.minRow; r <= n.maxRow; r++) {
          const cells: string[] = [];
          for (let c = n.minCol; c <= n.maxCol; c++) {
            const col = columns[c];
            cells.push(String(rows[r]?.[col.key] ?? ""));
          }
          lines.push(cells.join("\t"));
        }
        navigator.clipboard.writeText(lines.join("\n")).catch(() => {});
        const newRows = [...rows];
        for (let r = n.minRow; r <= n.maxRow; r++) {
          if (rowStates[r]?.status === "success" || rowStates[r]?.status === "emitting") continue;
          for (let c = n.minCol; c <= n.maxCol; c++) {
            const col = columns[c];
            if (col && newRows[r]) {
              newRows[r] = { ...newRows[r], [col.key]: "" };
            }
          }
        }
        onChange(newRows);
        toast.info("Cortado al portapapeles", { duration: 1500 });
        return;
      }

      // Ctrl + C: Copiar selección
      if (e.key === "c" || e.key === "C") {
        e.preventDefault();
        const lines: string[] = [];
        for (let r = n.minRow; r <= n.maxRow; r++) {
          const cells: string[] = [];
          for (let c = n.minCol; c <= n.maxCol; c++) {
            const col = columns[c];
            cells.push(String(rows[r]?.[col.key] ?? ""));
          }
          lines.push(cells.join("\t"));
        }
        const clipboardText = lines.join("\n");
        navigator.clipboard.writeText(clipboardText).catch(() => {});
        return;
      }
    }

    // ── Navegación con Flechas (Shift expande, flecha sola mueve) ──
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (e.shiftKey) {
        setSelection({
          ...selection,
          endRow: e.ctrlKey || e.metaKey ? 0 : Math.max(0, selection.endRow - 1),
        });
      } else {
        const targetRow = e.ctrlKey || e.metaKey ? 0 : Math.max(0, selection.startRow - 1);
        setSelection({ startRow: targetRow, startCol: selection.startCol, endRow: targetRow, endCol: selection.startCol });
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (e.shiftKey) {
        setSelection({
          ...selection,
          endRow: e.ctrlKey || e.metaKey ? rows.length - 1 : Math.min(rows.length - 1, selection.endRow + 1),
        });
      } else {
        const targetRow = e.ctrlKey || e.metaKey ? rows.length - 1 : Math.min(rows.length - 1, selection.startRow + 1);
        setSelection({ startRow: targetRow, startCol: selection.startCol, endRow: targetRow, endCol: selection.startCol });
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (e.shiftKey) {
        setSelection({
          ...selection,
          endCol: e.ctrlKey || e.metaKey ? 0 : Math.max(0, selection.endCol - 1),
        });
      } else {
        const targetCol = e.ctrlKey || e.metaKey ? 0 : Math.max(0, selection.startCol - 1);
        setSelection({ startRow: selection.startRow, startCol: targetCol, endRow: selection.startRow, endCol: targetCol });
      }
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      if (e.shiftKey) {
        setSelection({
          ...selection,
          endCol: e.ctrlKey || e.metaKey ? columns.length - 1 : Math.min(columns.length - 1, selection.endCol + 1),
        });
      } else {
        const targetCol = e.ctrlKey || e.metaKey ? columns.length - 1 : Math.min(columns.length - 1, selection.startCol + 1);
        setSelection({ startRow: selection.startRow, startCol: targetCol, endRow: selection.startRow, endCol: targetCol });
      }
    } else if (e.key === "Enter" || e.key === "F2") {
      e.preventDefault();
      startEditing(curRow, curCol);
    } else if (e.key === "Tab") {
      e.preventDefault();
      if (e.shiftKey) {
        if (curCol > 0) setSelection({ startRow: curRow, startCol: curCol - 1, endRow: curRow, endCol: curCol - 1 });
        else if (curRow > 0) setSelection({ startRow: curRow - 1, startCol: columns.length - 1, endRow: curRow - 1, endCol: columns.length - 1 });
      } else {
        if (curCol < columns.length - 1) setSelection({ startRow: curRow, startCol: curCol + 1, endRow: curRow, endCol: curCol + 1 });
        else if (curRow < rows.length - 1) setSelection({ startRow: curRow + 1, startCol: 0, endRow: curRow + 1, endCol: 0 });
      }
    } else if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      pushHistory(rows);
      const newRows = [...rows];
      for (let r = n.minRow; r <= n.maxRow; r++) {
        if (rowStates[r]?.status === "success" || rowStates[r]?.status === "emitting") continue;
        for (let c = n.minCol; c <= n.maxCol; c++) {
          const col = columns[c];
          if (col && newRows[r]) {
            newRows[r] = { ...newRows[r], [col.key]: "" };
          }
        }
      }
      onChange(newRows);
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // Start typing → enter edit mode with this character (only for non-select cells)
      const col = columns[curCol];
      if (col?.type !== "select") {
        e.preventDefault();
        setEditValue(e.key);
        setEditingCell({ row: curRow, col: curCol });
      }
    }
  };

  // ── Paste handler (on hidden input) ──
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData("text");
    if (!text) return;
    if (!isMultiCellData(text)) {
      if (!selection) return;
      e.preventDefault();
      const n = normalizeSelection(selection);
      const val = text.trim();

      // Pegado en una sola celda
      if (n.minRow === n.maxRow && n.minCol === n.maxCol) {
        const col = columns[n.minCol];
        if (!col) return;
        if (col.dynamicOptions === "region" && val) {
          const matched = matchRegion(val, regiones);
          updateCell(n.minRow, col.key, matched ?? "");
          if (!matched) toast.error("Región no reconocida");
          return;
        }
        if (col.dynamicOptions === "comuna" && val) {
          const regionColIdx = columns.findIndex((c) => c.dynamicOptions === "region");
          const rowRegion = regionColIdx >= 0 ? String(rows[n.minRow][columns[regionColIdx].key] ?? "") : "";
          const matched = matchComuna(val, comunas, rowRegion || undefined);
          updateCell(n.minRow, col.key, matched ?? "");
          if (!matched) toast.error("Comuna no reconocida");
          return;
        }
        let finalVal = col.type === "date" ? normalizeDate(val) : val;
        if (col.type !== "number" && col.type !== "date" && col.type !== "select" && col.validate !== "rut") {
          const { cleaned, hadInvalid } = sanitizeBheText(finalVal);
          if (hadInvalid) {
            toast.info("Ajustamos caracteres no permitidos por el SII (# → N°, & → y, etc.)", { duration: 3000 });
            finalVal = cleaned;
          }
        }
        updateCell(n.minRow, col.key, finalVal);
        return;
      }

      // Multi-paste: pegar un valor único en todas las celdas del rango seleccionado
      pushHistory(rows);
      const newRows = [...rows];
      const regionColIdx = columns.findIndex((c) => c.dynamicOptions === "region");
      let hadSanitized = false;
      for (let r = n.minRow; r <= n.maxRow; r++) {
        if (rowStates[r]?.status === "success" || rowStates[r]?.status === "emitting") continue;
        newRows[r] = { ...newRows[r] };
        for (let c = n.minCol; c <= n.maxCol; c++) {
          const col = columns[c];
          if (!col) continue;
          let finalVal = col.type === "date" ? normalizeDate(val) : val;
          if (col.validate === "rut") {
            (newRows[r][col.key] as unknown) = finalVal ? formatRut(finalVal) : "";
          } else if (col.dynamicOptions === "region") {
            const matched = matchRegion(val, regiones);
            (newRows[r][col.key] as unknown) = matched ?? "";
          } else if (col.dynamicOptions === "comuna") {
            const rowRegion = regionColIdx >= 0 ? String(newRows[r][columns[regionColIdx].key] ?? "") : "";
            const matched = matchComuna(val, comunas, rowRegion || undefined);
            (newRows[r][col.key] as unknown) = matched ?? "";
          } else {
            if (col.type !== "number" && col.type !== "date" && col.type !== "select") {
              const { cleaned, hadInvalid } = sanitizeBheText(finalVal);
              if (hadInvalid) hadSanitized = true;
              finalVal = cleaned;
            }
            (newRows[r][col.key] as unknown) = finalVal;
          }
        }
      }
      onChange(newRows);
      if (hadSanitized) {
        toast.info("Ajustamos caracteres no permitidos por el SII (# → N°, & → y, etc.)", { duration: 3500 });
      }
      const totalCells = (n.maxRow - n.minRow + 1) * (n.maxCol - n.minCol + 1);
      toast.success(`Valor pegado en ${totalCells} celdas`, { duration: 1500 });
      return;
    }

    e.preventDefault();
    pushHistory(rows);
    const parsed = parseClipboard(text);

    // Detect header row
    const firstRowMatchesHeader = parsed[0]?.every((cell, ci) =>
      cell.toLowerCase().trim() === String(columns[ci]?.label ?? "").toLowerCase().trim()
    );
    const dataRows = firstRowMatchesHeader ? parsed.slice(1) : parsed;

    const startRow = selection ? normalizeSelection(selection).minRow : 0;
    const startCol = selection ? normalizeSelection(selection).minCol : 0;

    const newRows = [...rows];
    const newStates = [...rowStates];

    let currentRow = startRow;
    let invalidRuts = 0;
    let unmatchedRegion = 0;
    let unmatchedComuna = 0;
    let hadSanitized = false;
    const regionColIdx = columns.findIndex((c) => c.dynamicOptions === "region");
    for (const cells of dataRows) {
      if (currentRow >= maxRows) break;
      if (!newRows[currentRow]) {
        newRows[currentRow] = emptyRow();
        newStates[currentRow] = { status: "idle" };
      }
      const row = { ...newRows[currentRow] };
      cells.forEach((cellVal, ci) => {
        const targetCol = startCol + ci;
        if (targetCol < columns.length) {
          const col = columns[targetCol];
          const rawVal = cellVal.trim();
          let finalVal = col.type === "date" ? normalizeDate(rawVal) : rawVal;
          if (col.validate === "rut" && finalVal) {
            const formatted = formatRut(finalVal);
            (row[col.key] as unknown) = formatted;
            if (!validarRut(finalVal)) {
              invalidRuts++;
              newStates[currentRow] = { status: "error", error: `RUT inválido en ${col.label}` };
            }
          } else if (col.dynamicOptions === "region" && rawVal) {
            const matched = matchRegion(rawVal, regiones);
            if (matched) {
              (row[col.key] as unknown) = matched;
            } else {
              (row[col.key] as unknown) = "";
              unmatchedRegion++;
            }
          } else if (col.dynamicOptions === "comuna" && rawVal) {
            const rowRegion = regionColIdx >= 0 ? String(row[columns[regionColIdx].key] ?? "") : "";
            const matched = matchComuna(rawVal, comunas, rowRegion || undefined);
            if (matched) {
              (row[col.key] as unknown) = matched;
            } else {
              (row[col.key] as unknown) = "";
              unmatchedComuna++;
            }
          } else {
            if (col.type !== "number" && col.type !== "date" && col.type !== "select") {
              const { cleaned, hadInvalid } = sanitizeBheText(finalVal);
              if (hadInvalid) hadSanitized = true;
              finalVal = cleaned;
            }
            (row[col.key] as unknown) = finalVal;
          }
        }
      });

      // Fill Blanks Only: si se pegó un RUT Receptor conocido, rellenar solo celdas vacías
      if (clientes) {
        const targetRut = String(row["receptorRut"] ?? "").replace(/[.\s-]/g, "").toUpperCase();
        if (targetRut && validarRut(targetRut)) {
          const currentEmisor = String(row["rutEmisor"] ?? "").replace(/[.\s-]/g, "").toUpperCase();
          const matched = clientes.find((c) => {
            const cRut = c.rut.replace(/[.\s-]/g, "").toUpperCase();
            if (cRut !== targetRut) return false;
            if (currentEmisor) {
              const cEmisor = c.rutEmisor.replace(/[.\s-]/g, "").toUpperCase();
              return cEmisor === currentEmisor;
            }
            return true;
          });
          if (matched) {
            if (!String(row["receptorNombre"] ?? "").trim() && matched.razonSocial) {
              (row["receptorNombre"] as unknown) = matched.razonSocial;
            }
            if (!String(row["receptorGiro"] ?? "").trim() && matched.giro) {
              (row["receptorGiro"] as unknown) = matched.giro;
            }
            if (!String(row["receptorDireccion"] ?? "").trim() && matched.direccion) {
              (row["receptorDireccion"] as unknown) = matched.direccion;
            }
            if (!String(row["direccion"] ?? "").trim() && matched.direccion) {
              (row["direccion"] as unknown) = matched.direccion;
            }
            if (!String(row["receptorComuna"] ?? "").trim() && matched.comuna) {
              (row["receptorComuna"] as unknown) = matched.comuna;
            }
            if (!String(row["comuna"] ?? "").trim() && matched.comuna) {
              (row["comuna"] as unknown) = matched.comuna;
            }
            if (!String(row["correo"] ?? "").trim() && matched.email) {
              (row["correo"] as unknown) = matched.email;
            }
            if (!String(row["telefono"] ?? "").trim() && matched.telefono) {
              (row["telefono"] as unknown) = matched.telefono;
            }
            if (matched.comuna && !String(row["receptorRegion"] ?? "").trim()) {
              const matchedCom = matchComuna(matched.comuna, comunas);
              if (matchedCom) {
                const found = comunas.find((c) => c.nombre.toLowerCase() === matchedCom.toLowerCase());
                if (found) (row["receptorRegion"] as unknown) = found.regionNombre;
              }
            }
          }
        }
      }

      newRows[currentRow] = row;
      currentRow++;
    }

    if (invalidRuts > 0) {
      toast.warning(`${invalidRuts} RUT(s) con dígito verificador inválido fueron pegados y destacados en rojo para su corrección.`);
    }
    if (unmatchedRegion > 0) {
      toast.error(`${unmatchedRegion} región(es) no reconocida(s) al pegar`);
    }
    if (unmatchedComuna > 0) {
      toast.error(`${unmatchedComuna} comuna(s) no reconocida(s) al pegar`);
    }

    // Auto-ordenar por columna destacada (ej: RUT Emisor) si hay >1 valor distinto
    const highlightCol = columns.find((c) => c.highlight);
    if (highlightCol) {
      const sorted = sortByHighlightCol(newRows, newStates, highlightCol.key);
      if (sorted.distinctCount > 1) {
        newRows.splice(0, newRows.length, ...sorted.rows);
        newStates.splice(0, newStates.length, ...sorted.states);
        toast.info(`Se ordenaron tus filas por ${highlightCol.label} (${sorted.distinctCount} valores distintos)`);
      }
    }

    onChange(newRows);
    onRowStatesChange(newStates);

    // Update selection to cover pasted range
    const pastedRows = dataRows.length;
    const pastedCols = Math.min(dataRows[0]?.length ?? 1, columns.length - startCol);
    setSelection({
      startRow,
      startCol,
      endRow: Math.min(startRow + pastedRows - 1, maxRows - 1),
      endCol: Math.min(startCol + pastedCols - 1, columns.length - 1),
    });
  }, [rows, rowStates, columns, emptyRow, maxRows, onChange, onRowStatesChange, selection]);

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      const parsed = parseClipboard(text);
      const firstRowMatchesHeader = parsed[0]?.every((cell, ci) =>
        cell.toLowerCase().trim() === String(columns[ci]?.label ?? "").toLowerCase().trim()
      );
      const dataRows = firstRowMatchesHeader ? parsed.slice(1) : parsed;
      pushHistory(rows);
      const newRows: T[] = [];
      const newStates: RowState[] = [];
      let invalidRuts = 0;
      let unmatchedRegion = 0;
      let unmatchedComuna = 0;
      const regionColIdx = columns.findIndex((c) => c.dynamicOptions === "region");
      for (const cells of dataRows.slice(0, maxRows)) {
        const row = emptyRow();
        let rowHasRutError = false;
        columns.forEach((col, ci) => {
          if (cells[ci] !== undefined && cells[ci] !== "") {
            const val = cells[ci].trim();
            if (col.validate === "rut" && val) {
              const formatted = formatRut(val);
              (row[col.key] as unknown) = formatted;
              if (!validarRut(val)) {
                invalidRuts++;
                rowHasRutError = true;
              }
            } else if (col.dynamicOptions === "region" && val) {
              const matched = matchRegion(val, regiones);
              if (matched) {
                (row[col.key] as unknown) = matched;
              } else {
                (row[col.key] as unknown) = "";
                unmatchedRegion++;
              }
            } else if (col.dynamicOptions === "comuna" && val) {
              const rowRegion = regionColIdx >= 0 ? String(row[columns[regionColIdx].key] ?? "") : "";
              const matched = matchComuna(val, comunas, rowRegion || undefined);
              if (matched) {
                (row[col.key] as unknown) = matched;
              } else {
                (row[col.key] as unknown) = "";
                unmatchedComuna++;
              }
            } else {
              (row[col.key] as unknown) = col.type === "date" ? normalizeDate(val) : val;
            }
          }
        });
        newRows.push(row);
        newStates.push(rowHasRutError ? { status: "error", error: "RUT inválido detectado" } : { status: "idle" });
      }
      if (invalidRuts > 0) {
        toast.warning(`${invalidRuts} RUT(s) con dígito verificador inválido fueron importados y destacados en rojo para su corrección.`);
      }
      if (unmatchedRegion > 0) {
        toast.error(`${unmatchedRegion} región(es) no reconocida(s) en el CSV`);
      }
      if (unmatchedComuna > 0) {
        toast.error(`${unmatchedComuna} comuna(s) no reconocida(s) en el CSV`);
      }

      // Auto-ordenar por columna destacada (ej: RUT Emisor) si hay >1 valor distinto
      const highlightCol = columns.find((c) => c.highlight);
      const combinedRows = [...rows, ...newRows];
      const combinedStates = [...rowStates, ...newStates];
      if (highlightCol) {
        const sorted = sortByHighlightCol(combinedRows, combinedStates, highlightCol.key);
        if (sorted.distinctCount > 1) {
          toast.info(`Se ordenaron tus filas por ${highlightCol.label} (${sorted.distinctCount} valores distintos)`);
          onChange(sorted.rows);
          onRowStatesChange(sorted.states);
          return;
        }
      }
      onChange(combinedRows);
      onRowStatesChange(combinedStates);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const validRows = validateRow
    ? rows.filter((r) => !validateRow(r)).length
    : rows.filter((r) => columns.every((c) => !c.required || String(r[c.key] ?? "").trim())).length;

  const cellDisplayClass = "w-full h-8 px-2 text-sm flex items-center cursor-default select-none overflow-hidden whitespace-nowrap text-ellipsis";

  const getCellBg = (rowIdx: number, colIdx: number): string => {
    if (editingCell?.row === rowIdx && editingCell?.col === colIdx) return "";
    const isHighlight = columns[colIdx]?.highlight;
    if (selection && isCellInSelection(rowIdx, colIdx, selection)) {
      const n = normalizeSelection(selection);
      if (rowIdx === n.minRow && colIdx === n.minCol) return "bg-primary/25 ring-1 ring-primary/50 inset-ring inset-ring-primary/30";
      return "bg-primary/15";
    }
    const isError = rowStates[rowIdx]?.status === "error";
    if (isError) {
      return isHighlight ? "bg-destructive/15 hover:bg-destructive/20" : "bg-destructive/5 hover:bg-destructive/10";
    }
    return isHighlight ? "bg-destructive/5 hover:bg-destructive/10" : "hover:bg-muted/20";
  };

  const selectionSummary = (() => {
    if (!selection) return null;
    const n = normalizeSelection(selection);
    const cellCount = (n.maxRow - n.minRow + 1) * (n.maxCol - n.minCol + 1);
    if (cellCount <= 1) return null;

    let sum = 0;
    let numericCount = 0;

    for (let r = n.minRow; r <= n.maxRow; r++) {
      for (let c = n.minCol; c <= n.maxCol; c++) {
        const col = columns[c];
        if (!col) continue;
        const rawVal = rows[r]?.[col.key];
        if (rawVal !== undefined && rawVal !== null && rawVal !== "") {
          const num = Number(String(rawVal).replace(/\./g, "").replace(/,/g, "."));
          if (!isNaN(num) && (col.type === "number" || /monto|total|neto|iva|precio|valor/i.test(String(col.key)))) {
            sum += num;
            numericCount++;
          }
        }
      }
    }

    return {
      count: cellCount,
      sum: numericCount > 0 ? sum : null,
      avg: numericCount > 1 ? sum / numericCount : null,
    };
  })();

  return (
    <div className="space-y-3">
      {/* Hidden input for keyboard/paste capture */}
      <input
        ref={hiddenInputRef}
        className="sr-only"
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onChange={() => {}}
        value=""
        tabIndex={-1}
      />

      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          {/* Controles de Deshacer / Rehacer */}
          <div className="flex items-center gap-1 border-r border-border pr-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={undo}
              disabled={!canUndo}
              title="Deshacer (Ctrl+Z)"
              className="h-7 px-2 text-xs gap-1 cursor-pointer"
            >
              <Undo2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Deshacer</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={redo}
              disabled={!canRedo}
              title="Rehacer (Ctrl+Y)"
              className="h-7 px-2 text-xs gap-1 cursor-pointer"
            >
              <Redo2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Rehacer</span>
            </Button>
          </div>

          <span className="text-sm text-muted-foreground">
            {validRows} de {rows.length} válidas
          </span>
          {canScrollRight && (
            <button
              type="button"
              onClick={() => tableRef.current?.scrollBy({ left: 350, behavior: "smooth" })}
              className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 border border-primary/20 rounded-md transition-all cursor-pointer shadow-2xs hover:scale-102"
              title="Desplazar tabla para ver más columnas a la derecha"
            >
              <span>Hay más columnas</span>
              <ChevronRight className="h-3.5 w-3.5 animate-pulse" />
            </button>
          )}
          {canScrollLeft && (
            <button
              type="button"
              onClick={() => tableRef.current?.scrollBy({ left: -350, behavior: "smooth" })}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-muted-foreground bg-muted/60 hover:bg-muted border border-border rounded-md transition-all cursor-pointer"
              title="Volver a columnas anteriores"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              <span>Anteriores</span>
            </button>
          )}
        </div>

        {/* Resumen dinámico estilo Excel cuando hay selección de celdas */}
        {selectionSummary && (
          <div className="flex items-center gap-2 text-xs bg-muted/70 px-2.5 py-1 rounded-md border border-border font-mono shadow-2xs">
            {selectionSummary.sum !== null && (
              <span className="font-semibold text-foreground">
                Σ Suma: ${Math.round(selectionSummary.sum).toLocaleString("es-CL")}
              </span>
            )}
            {selectionSummary.avg !== null && (
              <span className="text-muted-foreground">
                | Prom: ${Math.round(selectionSummary.avg).toLocaleString("es-CL")}
              </span>
            )}
            <span className="text-muted-foreground">
              ({selectionSummary.count} celdas)
            </span>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-md border-2 border-dashed border-muted-foreground/30 p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Pega datos desde Excel (Ctrl+V), o sube un CSV.
          </p>
        </div>
      ) : (
        <div className="relative rounded-md border border-border overflow-hidden">
          {/* Sombra y botón interactivo lateral izquierdo */}
          {canScrollLeft && (
            <>
              <div
                className="pointer-events-none absolute left-0 top-0 bottom-0 w-12 z-20 bg-gradient-to-r from-background/95 via-background/40 to-transparent transition-opacity duration-300"
                aria-hidden="true"
              />
              <button
                type="button"
                onClick={() => tableRef.current?.scrollBy({ left: -350, behavior: "smooth" })}
                className="absolute left-1.5 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center h-8 w-8 rounded-full bg-background/90 hover:bg-background text-foreground border border-border shadow-md backdrop-blur-xs transition-all hover:scale-110 active:scale-95 cursor-pointer group"
                title="Desplazar tabla a la izquierda"
              >
                <ChevronLeft className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </button>
            </>
          )}

          {/* Sombra y botón interactivo lateral derecho */}
          {canScrollRight && (
            <>
              <div
                className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 z-20 bg-gradient-to-l from-background/95 via-background/40 to-transparent transition-opacity duration-300"
                aria-hidden="true"
              />
              <button
                type="button"
                onClick={() => tableRef.current?.scrollBy({ left: 350, behavior: "smooth" })}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center h-8 w-8 rounded-full bg-background/90 hover:bg-background text-foreground border border-border shadow-md backdrop-blur-xs transition-all hover:scale-110 active:scale-95 cursor-pointer group"
                title="Desplazar tabla a la derecha"
              >
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors animate-pulse" />
              </button>
            </>
          )}

          <div
            ref={tableRef}
            onScroll={updateScrollIndicators}
            className="overflow-x-auto"
          >
            <table className="w-full text-sm border-collapse">
              <thead className="bg-muted/50">
                <tr>
                  <th className="w-8 border-r border-border/60 px-1 py-1.5 text-center font-medium text-xs text-muted-foreground">#</th>
                  {columns.map((col) => (
                    <th
                      key={String(col.key)}
                      className={`border-r border-border/60 px-2 py-1.5 text-left font-medium text-xs whitespace-nowrap ${col.highlight ? "bg-destructive/10 text-destructive border-b-2 border-destructive/40" : "text-muted-foreground"}`}
                      style={{ width: col.width }}
                      title={col.help}
                    >
                      {col.label}{col.required && <span className="text-destructive ml-0.5">*</span>}
                      {col.help && <span title={col.help}><Info className="inline-block ml-1 h-3 w-3 text-muted-foreground/60 cursor-help align-middle" /></span>}
                    </th>
                  ))}
                  <th className="min-w-[130px] w-36 border-r border-border/60 px-2 py-1.5 text-center font-medium text-xs text-muted-foreground">Estado</th>
                  <th className="w-8 px-1 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => {
                  const regionCol = columns.find((c) => c.dynamicOptions === "region");
                  const rowRegion = regionCol ? String(row[regionCol.key] ?? "") : "";
                  const filteredComunas = rowRegion ? comunas.filter((c) => c.regionNombre === rowRegion) : comunas;
                  const hasError = rowStates[rowIdx]?.status === "error";
                  return (
                  <tr
                    key={rowIdx}
                    data-row={rowIdx}
                    className={`border-t border-border/60 transition-colors ${hasError ? "bg-destructive/10" : ""}`}
                  >
                    <td className={`border-r border-border/60 px-1 py-0 text-center text-xs select-none ${hasError ? "border-l-4 border-l-destructive font-semibold text-destructive bg-destructive/15" : "text-muted-foreground/70"}`}>
                      {rowIdx + 1}
                    </td>
                    {columns.map((col, colIdx) => {
                      const isEditing = editingCell?.row === rowIdx && editingCell?.col === colIdx;
                      const cellValue = String(row[col.key] ?? "");
                      const isSelectCol = col.type === "select";

                      if (isSelectCol) {
                        const isDisabled = rowStates[rowIdx]?.status === "success" || rowStates[rowIdx]?.status === "emitting";
                        return (
                          <td
                            key={String(col.key)}
                            data-row={rowIdx}
                            data-col={colIdx}
                            data-col-key={String(col.key)}
                            className={`border-r border-border/60 p-0 relative transition-all duration-300 ${getCellBg(rowIdx, colIdx)}`}
                            onClick={() => setSelection({ startRow: rowIdx, startCol: colIdx, endRow: rowIdx, endCol: colIdx })}
                          >
                          <div className="relative w-full h-8 flex items-center pr-1">
                            <select
                              value={cellValue}
                              disabled={isDisabled}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (col.dynamicOptions === "region") {
                                  const comunaCol = columns.find((c) => c.dynamicOptions === "comuna");
                                  const updates: Record<string, string> = { [String(col.key)]: val };
                                  if (comunaCol) updates[String(comunaCol.key)] = "";
                                  updateCells(rowIdx, updates as unknown as Partial<Record<keyof T, string>>);
                                } else if (col.dynamicOptions === "comuna") {
                                  const updates: Record<string, string> = { [String(col.key)]: val };
                                  if (val && !rowRegion && regionCol) {
                                    const found = comunas.find((c) => c.nombre === val);
                                    if (found) updates[String(regionCol.key)] = found.regionNombre;
                                  }
                                  updateCells(rowIdx, updates as unknown as Partial<Record<keyof T, string>>);
                                } else {
                                  updateCell(rowIdx, col.key, val);
                                }
                              }}
                              onFocus={() => {
                                setSelection({ startRow: rowIdx, startCol: colIdx, endRow: rowIdx, endCol: colIdx });
                              }}
                              className={`w-full h-full px-2 text-xs bg-transparent border-0 outline-none cursor-pointer focus:bg-background focus:ring-1 focus:ring-primary/50 text-foreground transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${
                                !cellValue ? "text-muted-foreground/60 italic" : "font-medium"
                              }`}
                            >
                              <option value="" disabled hidden className="text-muted-foreground">
                                {col.placeholder ?? `Seleccionar ${col.label.toLowerCase()}...`}
                              </option>
                              {col.dynamicOptions === "region"
                                ? regiones.map((r) => <option key={r} value={r} className="text-foreground bg-popover py-1">{r}</option>)
                                : col.dynamicOptions === "comuna"
                                ? filteredComunas.map((c) => <option key={c.nombre} value={c.nombre} className="text-foreground bg-popover py-1">{c.nombre}</option>)
                                : col.options?.map((opt) => (
                                    <option key={opt.value} value={opt.value} className="text-foreground bg-popover py-1">{opt.label}</option>
                                  ))
                              }
                            </select>
                          </div>
                        </td>
                      );
                    }

                    return (
                      <td
                        key={String(col.key)}
                        data-row={rowIdx}
                        data-col={colIdx}
                        data-col-key={String(col.key)}
                        className={`border-r border-border/60 p-0 relative transition-all duration-300 ${getCellBg(rowIdx, colIdx)}`}
                        onMouseDown={(e) => !isEditing && handleCellMouseDown(e, rowIdx, colIdx)}
                        onMouseEnter={() => handleCellMouseEnter(rowIdx, colIdx)}
                        onDoubleClick={() => startEditing(rowIdx, colIdx)}
                      >
                        {isEditing ? (
                          <>
                            <input
                              type={col.type === "number" ? "number" : col.type === "date" ? "date" : "text"}
                              placeholder={col.placeholder ?? col.label}
                              value={editValue}
                              autoFocus
                              onChange={(e) => {
                                if (col.validate === "rut") {
                                  const raw = e.target.value.replace(/[^\dKk]/g, "").toUpperCase();
                                  if (raw.length <= 1) { setEditValue(raw); return; }
                                  const dv = raw.slice(-1);
                                  const body = raw.slice(0, -1);
                                  setEditValue(body.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "-" + dv);
                                } else {
                                  setEditValue(e.target.value);
                                }
                              }}
                              onBlur={commitEdit}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                                else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                                else if (e.key === "Tab") { e.preventDefault(); commitEdit(); }
                              }}
                              {...(col.type === "date" ? { max: todayChile() } : {})}
                              className="w-full h-8 px-2 text-sm bg-background border-0 outline-none ring-2 ring-primary/50"
                            />
                            {/* Popover de autocompletado flotante in-cell para receptorRut */}
                            {isEditing && col.key === "receptorRut" && clientes && editValue.replace(/[.\s-]/g, "").length >= 3 && (() => {
                              const cleanTerm = editValue.replace(/[.\s-]/g, "").toUpperCase();
                              const currentEmisor = String(row["rutEmisor"] ?? "").replace(/[.\s-]/g, "").toUpperCase();
                              const matches = clientes.filter((c) => {
                                const cRut = c.rut.replace(/[.\s-]/g, "").toUpperCase();
                                const cNom = (c.razonSocial ?? "").toUpperCase();
                                if (!cRut.includes(cleanTerm) && !cNom.includes(cleanTerm)) return false;
                                if (currentEmisor) {
                                  const cEmisor = c.rutEmisor.replace(/[.\s-]/g, "").toUpperCase();
                                  return cEmisor === currentEmisor;
                                }
                                return true;
                              }).slice(0, 5);

                              if (matches.length === 0) return null;

                              return (
                                <div className="absolute left-0 top-full z-50 mt-0.5 w-64 rounded-md border border-border bg-popover text-popover-foreground shadow-lg divide-y divide-border/40 py-1 text-left">
                                  {matches.map((sug) => (
                                    <div
                                      key={sug.id}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        updateCell(rowIdx, col.key, formatRut(sug.rut));
                                        onSelectClienteInRow?.(rowIdx, sug);
                                        setEditingCell(null);
                                      }}
                                      className="px-2.5 py-1.5 hover:bg-accent cursor-pointer text-xs flex flex-col gap-0.5"
                                    >
                                      <span className="font-semibold text-foreground truncate">{sug.razonSocial}</span>
                                      <span className="font-mono text-[10px] text-muted-foreground">{formatRut(sug.rut)} {sug.comuna ? `• ${sug.comuna}` : ""}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </>
                        ) : (() => {
                          const isInvalidRut = col.validate === "rut" && !!cellValue.trim() && !validarRut(cellValue.trim());
                          if (isInvalidRut) {
                            return (
                              <div
                                className={`${cellDisplayClass} text-destructive font-semibold bg-destructive/15 ring-1 ring-inset ring-destructive/40 flex items-center justify-between`}
                                title={`⚠️ RUT inválido: "${cellValue}". Haz doble clic para corregir el dígito verificador.`}
                              >
                                <span className="truncate">{formatRut(cellValue)}</span>
                                <span className="text-[9px] font-bold bg-destructive text-destructive-foreground px-1 py-0.2 rounded ml-1 shrink-0">
                                  RUT INVÁLIDO
                                </span>
                              </div>
                            );
                          }
                          return (
                            <div className={cellDisplayClass} title={cellValue}>
                              {col.validate === "rut" && cellValue ? formatRut(cellValue) : cellValue || <span className="text-muted-foreground/40">{col.placeholder ?? col.label}</span>}
                            </div>
                          );
                        })()}
                      </td>
                    );
                  })}
                  <td className="border-r border-border/60 px-1 py-0 text-center">
                    {statusBadge(rowStates[rowIdx]?.status ?? "idle", rowStates[rowIdx]?.folio, rowStates[rowIdx]?.error)}
                  </td>
                  <td className="px-1 py-0 text-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); removeRow(rowIdx); }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="text-muted-foreground/40 hover:text-destructive transition-colors p-1"
                      title="Eliminar fila"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    )}

      <div className="flex items-center justify-center gap-2 flex-wrap mt-3">
        {onOpenClienteSelector && (
          <Button size="sm" variant="outline" onClick={onOpenClienteSelector}>
            <UserPlus className="mr-1 h-4 w-4" /> Cliente
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={addRow} disabled={rows.length >= maxRows}>
          <Plus className="mr-1 h-4 w-4" /> Fila
        </Button>
        <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
          <Upload className="mr-1 h-4 w-4" /> CSV
        </Button>
        <input ref={fileInputRef} type="file" accept=".csv,.txt" onChange={handleCsvUpload} className="hidden" />
        {rows.length > 0 && (
          <Button size="sm" variant="ghost" onClick={clearAll}>
            <Trash2 className="mr-1 h-4 w-4" /> Limpiar
          </Button>
        )}
      </div>
    </div>
  );
}
